import { m } from "framer-motion";
import { Leaf, PiggyBank, Clock, Heart, Recycle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import screenshotOverview from "@/assets/why-overview-device.webp";
import screenshotOverview320 from "@/assets/why-overview-device-320.webp";
import screenshotOverview500 from "@/assets/why-overview-device-500.webp";
import screenshotOverview750 from "@/assets/why-overview-device-750.webp";

const WhySection = () => {
  const { t, language } = useLanguage();

  const benefits = [
    {
      icon: Leaf,
      title: t("why.waste.title"),
      description: t("why.waste.desc"),
      color: "bg-fresh-green-light text-fresh-green",
    },
    {
      icon: PiggyBank,
      title: t("why.money.title"),
      description: t("why.money.desc"),
      color: "bg-warm-orange-light text-warm-orange",
    },
    {
      icon: Clock,
      title: t("why.time.title"),
      description: t("why.time.desc"),
      color: "bg-fresh-green-light text-fresh-green",
    },
    {
      icon: Heart,
      title: t("why.easy.title"),
      description: t("why.easy.desc"),
      color: "bg-warm-orange-light text-warm-orange",
    },
    {
      icon: Recycle,
      title: t("why.sustainable.title"),
      description: t("why.sustainable.desc"),
      color: "bg-fresh-green-light text-fresh-green",
    },
  ];

  return (
    <section
      id="benefits"
      className="py-16 lg:py-32 bg-background relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-fresh-green-light/30 to-transparent pointer-events-none" />

      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image / Mockup */}
          <m.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="relative order-2 lg:order-1"
          >
            <div className="relative max-w-md mx-auto">
              <img
                src={screenshotOverview500}
                srcSet={`${screenshotOverview320} 320w, ${screenshotOverview500} 500w, ${screenshotOverview750} 750w, ${screenshotOverview} 1013w`}
                sizes="(max-width: 640px) 256px, (max-width: 1024px) 288px, 448px"
                alt={t("why.imgAlt")}
                className="w-64 sm:w-72 lg:w-96 xl:w-[28rem] h-auto mx-auto relative"
                loading="lazy"
                decoding="async"
                width="448"
                height="849"
              />
            </div>
          </m.div>

          {/* Content */}
          <m.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="order-1 lg:order-2"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-fresh-green-light text-fresh-green text-sm font-medium mb-4">
              {t("why.badge")}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              {t("why.title")}
            </h2>
            <p className="text-lg text-muted-foreground mb-10">
              {t("why.subtitle")}
            </p>
            <p className="-mt-7 mb-8 text-sm text-muted-foreground">
              {language === "de" ? "Quelle und Definition: " : "Source and definition: "}
              <a className="underline underline-offset-4 hover:text-foreground" href="https://www.umweltbundesamt.de/themen/abfall-ressourcen/abfallwirtschaft/abfallvermeidung/lebensmittelabfaelle" target="_blank" rel="noopener noreferrer">
                Umweltbundesamt, Daten für 2022
              </a>
            </p>
            <p className="-mt-5 mb-8 text-sm text-muted-foreground">
              {language === "de"
                ? "Die Standard-Version ist werbefrei. Die Datenschutzerklärung nennt Frankfurt als Region der Cloud-Firestore-Datenbank und beschreibt weitere Firebase-Dienste sowie mögliche Datenübermittlungen."
                : "The standard version is ad-free. The privacy policy names Frankfurt as the Cloud Firestore database region and describes other Firebase services and possible data transfers."}{" "}
              <a className="underline underline-offset-4 hover:text-foreground" href={language === "de" ? "/datenschutz" : "/privacy-policy"}>
                {language === "de" ? "Datenschutzdetails" : "Privacy details"}
              </a>
            </p>

            <div className="space-y-5">
              {benefits.map((benefit, index) => (
                <m.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="flex items-start gap-4"
                >
                  <div
                    className={`w-12 h-12 rounded-xl ${benefit.color} flex items-center justify-center flex-shrink-0`}
                  >
                    <benefit.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">
                      {benefit.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {benefit.description}
                    </p>
                  </div>
                </m.div>
              ))}
            </div>
          </m.div>
        </div>
      </div>
    </section>
  );
};

export default WhySection;
